import { useEffect, useState, useCallback, useRef } from "react";
import { withUids } from "../lib/uid";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatINR, todayISO, API, formatDate } from "../lib/api";
import { Plus, Search, Printer, Trash2, Download, Share2, MessageCircle, Copy, MoreHorizontal, Link2, ArrowRight, Pencil, IndianRupee } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { UpiPayModal } from "../components/UpiPayModal";
import { generateLRPdf } from "../lib/pdf";
import { LRFormModal } from "../components/LRFormModal";
import { mapsSearchUrl, resolveCityState } from "../lib/maps";
import { toast } from "sonner";
import { formatApiErrorDetail } from "../lib/api";
import { usePolling } from "../lib/usePolling";
import { usePagination } from "../hooks/usePagination";
import { Pagination } from "../components/Pagination";
import { compressImage } from "../lib/image";
import { LifecycleBadge } from "../components/LifecycleBadge";
import { LRHandlers } from "../components/LRHandlers";

export function lrStatusClass(status) {
  if (["delivered", "paid"].includes(status)) return "badge-success";
  if (status === "cancelled") return "badge-error";
  if (["inv_generated", "inv_submitted"].includes(status)) return "badge-neutral";
  return "badge-pending";
}

const blank = {
  lr_number: "",  lr_date: todayISO(),
  consignor_name: "", consignor_address: "", consignor_gst: "",
  consignee_name: "", consignee_address: "", consignee_gst: "",
  hirer: { name: "", address: "", gst: "", contact_name: "", contact_phone: "", contact_email: "", division: "", division_address: "" },
  pickups: [], deliveries: [], transport_mode: "Road", road_load_type: "", ptl_legs: [],
  carrier: { company: "", pickup_poc_name: "", pickup_poc_phone: "", delivery_poc_name: "", delivery_poc_phone: "", notes: "" },
  flight_number: "", awb_number: "",
  from_location: "", to_location: "",
  goods_description: "", no_of_packages: 1, weight_kg: 0,
  vehicle_number: "", driver_name: "", driver_phone: "",
  vehicles: [],
  custom_fields: [],
  company_docs: [],
  documents: [],
  ewaybills: [],
  sub_transporter: { id: "", name: "", hub_name: "", poc_name: "", poc_phone: "", notes: "" },
  contacts: [],
  freight_amount: 0, quoted_amount: 0, payment_mode: "TBB",
  status: "in_transit", remarks: "", pod_image: "", delivered_at: "",
  pickup_maps_url: "", delivery_maps_url: "",
  company_brand: "ito_engineering",
  company_id: "", department_id: "", contact_id: "",
  items: [],
};

export default function LRs() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const qParam = searchParams.get("q") || "";
  const [items, setItems] = useState([]);
  const paged = usePagination(items, 25);
  const [q, setQ] = useState(qParam);
  useEffect(() => { setQ(qParam); }, [qParam]);
  const [statusFilter, setStatusFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [company, setCompany] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [subs, setSubs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [payModal, setPayModal] = useState(null);

  function openPayUpi(lr) {
    const veh = (lr.vehicles && lr.vehicles[0]) || {};
    setPayModal({
      payee_name: lr.driver_name || veh.broker_name || veh.driver_name || "",
      upi_id: "",
      phone: lr.driver_phone || veh.driver_phone || "",
      amount: "",
      note: `LR ${lr.lr_number || ""}`.trim(),
      category: "Freight",
      ref_type: "lr", ref_id: lr.id, ref_number: lr.lr_number || "",
    });
  }

  const load = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (statusFilter) params.status = statusFilter;
    const { data } = await api.get("/lrs", { params });
    setItems(data);
  }, [q, statusFilter]);

  const loadCustomers = useCallback(() => api.get("/customers").then(r => setCustomers(r.data)).catch(()=>{}), []);

  useEffect(() => { load(); }, [load]);
  usePolling(load, 8000, !showForm);
  // Open the edit modal directly when arriving from the LR detail page (?edit=<id>)
  const editParam = searchParams.get("edit") || "";
  useEffect(() => {
    if (!editParam) return;
    api.get(`/lrs/${editParam}`).then((r) => { openEdit(r.data); setSearchParams({}, { replace: true }); }).catch(() => setSearchParams({}, { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editParam]);
  useEffect(() => {
    api.get("/company").then(r => setCompany(r.data)).catch(()=>{});
    api.get("/vehicles").then(r => setVehicles(r.data)).catch(()=>{});
    api.get("/drivers").then(r => setDrivers(r.data)).catch(()=>{});
    api.get("/brokers").then(r => setBrokers(r.data)).catch(()=>{});
    api.get("/sub-transporters").then(r => setSubs(r.data)).catch(()=>{});
    api.get("/garv/users").then(r => setUsers(r.data)).catch(()=>{});
    loadCustomers();
  }, [loadCustomers]);

  // Auto-fill From/To (city, state of every pickup & delivery, combined) and the
  // pickup/delivery Google Maps links. Never clobbers values the user has edited.
  const autoFrom = useRef("");
  const autoTo = useRef("");
  const autoPickMaps = useRef("");
  const autoDelMaps = useRef("");
  const csCache = useRef({});
  useEffect(() => {
    let cancelled = false;
    async function run() {
      const pAddrs = (form.pickups || []).map((p) => (p.address || "").trim()).filter(Boolean);
      const dAddrs = (form.deliveries || []).map((p) => (p.address || "").trim()).filter(Boolean);
      for (const a of [...new Set([...pAddrs, ...dAddrs])]) {
        const key = a.toLowerCase();
        if (csCache.current[key] === undefined) {
          csCache.current[key] = "";
          csCache.current[key] = await resolveCityState(a);
        }
      }
      if (cancelled) return;
      const combine = (arr) => [...new Set(arr.map((a) => csCache.current[a.toLowerCase()]).filter(Boolean))].join(" + ");
      const fromVal = combine(pAddrs);
      const toVal = combine(dAddrs);
      setForm((f) => {
        let nf = f, changed = false;
        if (fromVal && (f.from_location === "" || f.from_location === autoFrom.current) && f.from_location !== fromVal) {
          nf = { ...nf, from_location: fromVal }; autoFrom.current = fromVal; changed = true;
        }
        if (toVal && (f.to_location === "" || f.to_location === autoTo.current) && f.to_location !== toVal) {
          nf = { ...nf, to_location: toVal }; autoTo.current = toVal; changed = true;
        }
        if (pAddrs[0] && (nf.pickup_maps_url === "" || nf.pickup_maps_url === autoPickMaps.current)) {
          const u = mapsSearchUrl(pAddrs[0]);
          if (u !== nf.pickup_maps_url) { nf = { ...nf, pickup_maps_url: u }; autoPickMaps.current = u; changed = true; }
        }
        if (dAddrs[dAddrs.length - 1] && (nf.delivery_maps_url === "" || nf.delivery_maps_url === autoDelMaps.current)) {
          const u = mapsSearchUrl(dAddrs[dAddrs.length - 1]);
          if (u !== nf.delivery_maps_url) { nf = { ...nf, delivery_maps_url: u }; autoDelMaps.current = u; changed = true; }
        }
        // Keep the legacy consignor/consignee fields (used by the PDF, search, etc.)
        // in sync with the pickup/delivery rows, which are now the source of truth.
        const pl = (f.pickups || [])[0];
        const dl = (f.deliveries || [])[(f.deliveries || []).length - 1];
        if (pl) {
          if ((pl.company || "") !== (nf.consignor_name || "")) { nf = { ...nf, consignor_name: pl.company || "" }; changed = true; }
          if ((pl.address || "") !== (nf.consignor_address || "")) { nf = { ...nf, consignor_address: pl.address || "" }; changed = true; }
          if ((pl.gst || "") !== (nf.consignor_gst || "")) { nf = { ...nf, consignor_gst: pl.gst || "" }; changed = true; }
        }
        if (dl) {
          if ((dl.company || "") !== (nf.consignee_name || "")) { nf = { ...nf, consignee_name: dl.company || "" }; changed = true; }
          if ((dl.address || "") !== (nf.consignee_address || "")) { nf = { ...nf, consignee_address: dl.address || "" }; changed = true; }
          if ((dl.gst || "") !== (nf.consignee_gst || "")) { nf = { ...nf, consignee_gst: dl.gst || "" }; changed = true; }
        }
        return changed ? nf : f;
      });
    }
    const timer = setTimeout(run, 700);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [form.pickups, form.deliveries]);

  const setH = (k, v) => setForm((f) => ({ ...f, hirer: { ...f.hirer, [k]: v } }));
  const setC = (k, v) => setForm((f) => ({ ...f, carrier: { ...f.carrier, [k]: v } }));
  function pickHirer(name) {
    const c = customers.find((x) => x.name === name);
    const ct = (c?.contacts || [])[0] || {};
    setForm((f) => {
      const next = { ...f, hirer: {
        ...f.hirer, name,
        gst: f.hirer.gst || c?.gst_number || "",
        address: f.hirer.address || c?.address || "",
        contact_name: f.hirer.contact_name || ct.name || "",
        contact_phone: f.hirer.contact_phone || ct.phone || "",
        contact_email: f.hirer.contact_email || ct.email || "",
        division: f.hirer.division || ct.division || "",
        division_address: f.hirer.division_address || ct.division_address || "",
      } };
      // Auto-link the "Link to Company Contact" picker to the SAME company when the
      // hirer matches a saved company, so orders track by company -> department -> person.
      // Department is then auto-matched by name inside the picker (see departmentHint).
      if (c && c.id !== f.company_id) {
        next.company_id = c.id;
        next.department_id = "";
        next.contact_id = "";
      }
      return next;
    });
  }
  const hirerContacts = (customers.find((c) => c.name === form.hirer?.name)?.contacts) || [];

  function mergeLabels(cf, labels) {
    const existing = new Set((cf || []).map((x) => (x.title || "").toLowerCase()));
    const add = (labels || []).filter((l) => l && !existing.has(l.toLowerCase())).map((l) => ({ title: l, value: "" }));
    return [...(cf || []), ...add];
  }
  function pickConsignor(name) {
    const c = customers.find((x) => x.name === name);
    setForm((f) => ({ ...f, consignor_name: name, consignor_gst: c?.gst_number || f.consignor_gst, consignor_address: c?.address || f.consignor_address, custom_fields: c ? mergeLabels(f.custom_fields, c.field_labels) : f.custom_fields }));
  }
  function pickConsignee(name) {
    const c = customers.find((x) => x.name === name);
    setForm((f) => ({ ...f, consignee_name: name, consignee_gst: c?.gst_number || f.consignee_gst, consignee_address: c?.address || f.consignee_address, custom_fields: c ? mergeLabels(f.custom_fields, c.field_labels) : f.custom_fields }));
  }

  // doc titles available for the LR documents uploader = defaults + matched customers' templates
  const lrDocTypes = (() => {
    const base = ["EWB", "AWB", "INV", "DC", "POD", "Other"];
    const extra = customers
      .filter((c) => c.name === form.consignor_name || c.name === form.consignee_name)
      .flatMap((c) => c.doc_titles || []);
    return Array.from(new Set([...extra, ...base]));
  })();

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function onPodFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { alert("POD image must be under 15 MB."); return; }
    try { set("pod_image", await compressImage(f)); }
    catch { const reader = new FileReader(); reader.onload = () => set("pod_image", String(reader.result)); reader.readAsDataURL(f); }
  }

  function pickDriver(name) {
    const d = drivers.find((x) => x.name === name);
    setForm((f) => ({ ...f, driver_name: name, driver_phone: d?.phone || f.driver_phone }));
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/lrs/${editingId}`, form);
      } else {
        await api.post("/lrs", form);
      }
      setShowForm(false);
      setForm(blank);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save LR");
    } finally {
      setSaving(false);
    }
  }

  async function remove(id) {
    if (!confirm("Delete this LR?")) return;
    await api.delete(`/lrs/${id}`);
    load();
  }

  async function shareTracking(lr) {
    try {
      const { data } = await api.post(`/lrs/${lr.id}/share-link`);
      const url = `${window.location.origin}${data.path}`;
      await navigator.clipboard.writeText(url);
      toast.success("Public tracking link copied — share with anyone (no login needed)");
    } catch {
      toast.error("Could not create tracking link");
    }
  }

  async function shareWhatsApp(lr) {
    try {
      const { data } = await api.post(`/lrs/${lr.id}/share-link`);
      const url = `${window.location.origin}${data.path}`;
      const msg = `Track your consignment *${lr.lr_number}*\n${lr.from_location || ""} -> ${lr.to_location || ""}\nStatus: ${String(lr.status || "").replace(/_/g, " ")}\n\n${url}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
    } catch {
      toast.error("Could not create tracking link");
    }
  }

  function openEdit(lr) {
    const seeded = (lr.vehicles && lr.vehicles.length > 0)
      ? lr.vehicles
      : (lr.vehicle_number ? [{ vehicle_number: lr.vehicle_number, driver_name: lr.driver_name || "", driver_phone: lr.driver_phone || "" }] : []);
    const norm = { ...blank, ...lr, vehicles: seeded };
    ["items", "vehicles", "pickups", "deliveries", "ptl_legs", "contacts", "custom_fields", "documents", "ewaybills"].forEach((k) => { norm[k] = withUids(norm[k]); });
    setForm(norm);
    // Seed the auto-derive refs with the saved values so changing a pickup/delivery
    // during edit re-derives From/To and the maps links (instead of treating the
    // loaded values as manual and never updating them).
    autoFrom.current = norm.from_location || "";
    autoTo.current = norm.to_location || "";
    autoPickMaps.current = norm.pickup_maps_url || "";
    autoDelMaps.current = norm.delivery_maps_url || "";
    setEditingId(lr.id);
    setShowForm(true);
  }

  function openNew() {
    autoFrom.current = ""; autoTo.current = ""; autoPickMaps.current = ""; autoDelMaps.current = "";
    setForm({ ...blank, vehicles: withUids([{ vehicle_number: "", driver_name: "", driver_phone: "" }]) });
    setEditingId(null);
    setShowForm(true);
    api.get("/lrs/next-number").then((r) => { if (r.data?.next) setForm((f) => (f.lr_number ? f : { ...f, lr_number: r.data.next })); }).catch(() => {});
  }

  function duplicateLr(lr) {
    // Clone into a fresh draft: keep all the data, drop identity/POD/tracking so a
    // brand-new LR (with the next number) is created on save.
    const clone = { ...blank, ...lr };
    ["id", "track_token", "pod_token", "created_at", "updated_at", "expense_total",
     "source_email_id", "work_started", "work_started_at", "delivered_at"].forEach((k) => delete clone[k]);
    clone.lr_number = "";
    clone.pod_image = "";
    clone.status = "in_transit";
    ["items", "vehicles", "pickups", "deliveries", "ptl_legs", "contacts", "custom_fields", "documents", "ewaybills"].forEach((k) => { clone[k] = withUids(clone[k]); });
    setForm(clone);
    setEditingId(null);
    setShowForm(true);
    api.get("/lrs/next-number").then((r) => { if (r.data?.next) setForm((f) => ({ ...f, lr_number: r.data.next })); }).catch(() => {});
    toast.success("Duplicated — review and save as a new LR");
  }

  const onHandlersSaved = (lrId, handlers) => setItems((prev) => prev.map((x) => (x.id === lrId ? { ...x, handlers } : x)));

  return (
    <div data-testid="lrs-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">Operations</div>
          <h1 className="font-headings text-4xl font-bold text-[#1A1A18] mt-1">Lorry Receipts</h1>
        </div>
        <div className="flex gap-2">
          <a href={`${API}/export/lrs.csv`} target="_blank" rel="noreferrer" className="btn-secondary" data-testid="export-lrs-csv"><Download className="w-4 h-4" /> Export CSV</a>
          <button className="btn-primary" onClick={openNew} data-testid="create-lr-button">
            <Plus className="w-4 h-4" /> New LR
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#68665E]" />
          <input className="input-field pl-9 w-72" placeholder="Search LR, consignor, vehicle..." value={q} onChange={(e)=>setQ(e.target.value)} data-testid="lr-search-input" />
        </div>
        <select className="input-field w-40" value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)} data-testid="lr-status-filter">
          <option value="">All status</option>
          <option value="loading">Loading</option>
          <option value="in_transit">In Transit</option>
          <option value="delivered">Delivered</option>
          <option value="inv_generated">Inv Generated</option>
          <option value="inv_submitted">Inv Submitted</option>
          <option value="paid">Paid</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <Pagination {...paged} testId="lrs-pagination" />
      <div className="table-wrap">
        <table className="w-full lrs-table">
          <thead>
            <tr>
              <th className="table-th nowrap">LR #</th>
              <th className="table-th nowrap">Date</th>
              <th className="table-th">Consignor → Consignee</th>
              <th className="table-th">Route</th>
              <th className="table-th nowrap">Vehicle</th>
              <th className="table-th nowrap text-right">Freight</th>
              <th className="table-th nowrap text-right">Expenses</th>
              <th className="table-th nowrap">Status</th>
              <th className="table-th">Handler(s)</th>
              <th className="table-th nowrap text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td className="table-td text-[#68665E] text-center" colSpan={10}>No LRs found.</td></tr>
            ) : paged.pageItems.map((lr) => (
              <tr key={lr.id} className="table-row" data-testid={`lr-row-${lr.id}`}>
                <td className="table-td nowrap font-mono text-xs"><button type="button" className="text-[#C84B31] hover:underline font-medium" onClick={()=>navigate(`/lr/${lr.id}`)} data-testid={`lr-link-${lr.id}`}>{lr.lr_number}</button></td>
                <td className="table-td nowrap">{formatDate(lr.lr_date)}</td>
                <td className="table-td min-w-[180px] max-w-[240px]">
                  <button type="button" className="hover:underline text-left font-medium block truncate w-full" title={lr.consignor_name} onClick={()=>navigate(`/entity/customer/${encodeURIComponent(lr.consignor_name)}`)} data-testid={`lr-consignor-link-${lr.id}`}>{lr.consignor_name}</button>
                  <div className="flex items-center gap-1 text-[hsl(var(--text-muted))] text-xs mt-0.5">
                    <ArrowRight className="w-3 h-3 shrink-0" />
                    <button type="button" className="hover:underline text-left truncate" title={lr.consignee_name} onClick={()=>navigate(`/entity/customer/${encodeURIComponent(lr.consignee_name)}`)}>{lr.consignee_name}</button>
                  </div>
                </td>
                <td className="table-td min-w-[140px] max-w-[200px]">
                  <div className="truncate" title={lr.from_location}>{lr.from_location}</div>
                  <div className="flex items-center gap-1 text-[hsl(var(--text-muted))] text-xs mt-0.5">
                    <ArrowRight className="w-3 h-3 shrink-0" />
                    <span className="truncate" title={lr.to_location}>{lr.to_location}</span>
                  </div>
                </td>
                <td className="table-td nowrap font-mono text-xs">{lr.vehicle_number || "—"}</td>
                <td className="table-td nowrap data-num font-medium text-right">{formatINR(lr.freight_amount)}</td>
                <td className="table-td nowrap data-num text-right text-[#8A2526]" data-testid={`lr-expense-${lr.id}`}>{Number(lr.expense_total || 0) > 0 ? formatINR(lr.expense_total) : "—"}</td>
                <td className="table-td nowrap">
                  <span className={lrStatusClass(lr.lifecycle?.effective_status || lr.status)}>
                    {String(lr.lifecycle?.effective_status || lr.status || "").replace(/_/g, " ")}
                  </span>
                  {lr.lifecycle && <div className="mt-1"><LifecycleBadge lifecycle={lr.lifecycle} /></div>}
                </td>
                <td className="table-td"><LRHandlers lr={lr} users={users} onNavigate={(uid)=>navigate(`/handling/${uid}`)} onSaved={onHandlersSaved} /></td>
                <td className="table-td nowrap text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button className="btn-ghost" title="Print / PDF" onClick={() => generateLRPdf(lr, company)} data-testid={`lr-print-${lr.id}`}><Printer className="w-4 h-4" /></button>
                    <button className="btn-ghost" title="Copy public tracking link" onClick={() => shareTracking(lr)} data-testid={`lr-share-track-${lr.id}`}><Share2 className="w-4 h-4" /></button>
                    <button className="btn-ghost" title="Edit" onClick={() => openEdit(lr)} data-testid={`lr-edit-${lr.id}`}><Pencil className="w-4 h-4" /></button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="btn-ghost" title="More actions" data-testid={`lr-more-${lr.id}`}><MoreHorizontal className="w-4 h-4" /></button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem onClick={() => openPayUpi(lr)} data-testid={`lr-pay-upi-${lr.id}`}>
                          <IndianRupee className="w-4 h-4 mr-2 text-[#C84B31]" /> Pay via UPI
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {lr.pod_token && (
                          <DropdownMenuItem onClick={() => { const url = `${window.location.origin}/p/pod/${lr.pod_token}`; navigator.clipboard.writeText(url); toast.success("Driver POD link copied"); }} data-testid={`lr-share-pod-${lr.id}`}>
                            <Link2 className="w-4 h-4 mr-2" /> Copy driver POD link
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => shareWhatsApp(lr)} data-testid={`lr-share-whatsapp-${lr.id}`}>
                          <MessageCircle className="w-4 h-4 mr-2 text-[#1f8a4c]" /> Share on WhatsApp
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => duplicateLr(lr)} data-testid={`lr-duplicate-${lr.id}`}>
                          <Copy className="w-4 h-4 mr-2" /> Duplicate LR
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => remove(lr.id)} className="text-[#8A2526] focus:text-[#8A2526]" data-testid={`lr-delete-${lr.id}`}>
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <LRFormModal
          form={form} set={set} setForm={setForm} setH={setH} setC={setC}
          pickHirer={pickHirer} hirerContacts={hirerContacts}
          customers={customers} vehicles={vehicles} drivers={drivers} brokers={brokers} subs={subs}
          onPodFile={onPodFile} editingId={editingId} saving={saving}
          save={save} lrDocTypes={lrDocTypes} reloadCustomers={loadCustomers}
          onClose={() => { setShowForm(false); setForm(blank); setEditingId(null); }}
        />
      )}

      <UpiPayModal open={!!payModal} prefill={payModal || {}} onClose={() => setPayModal(null)} onSaved={() => toast.success("Logged to UPI Payments")} />
    </div>
  );
}
